import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState, type DragEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { problemCode } from '@up2d8/api-client';
import type {
  ImportJob,
  ImportMapping,
  ImportRow,
  ImportRowPage,
  MappingOptions,
  MappingTemplate,
  PreviewResult,
  UploadResult,
} from '@up2d8/contracts';
import { cn, LoadingState, useToast } from '@up2d8/ui';
import {
  AdminIcon,
  AdminPage,
  Badge,
  Btn,
  Card,
  CardFoot,
  CardHead,
  CardPad,
  Checkbox,
  Empty,
  Field,
  Input,
  Modal,
  Note,
  PageHead,
  Select,
  Stats,
  Steps,
  Table,
  type BadgeTone,
} from '../admin-kit';
import { ConfirmModal } from '../organisatie/confirm-modal';
import { FilterMenu } from '../organisatie/filter-menu';
import '../organisatie/organisatie.css';
import { downloadFile } from '../../shared/download';
import { formatDateTime, localized } from '../../shared/format';
import { API_BASE, useSession } from '../../shared/session';
import { ListError } from '../../shared/list-error';
import { useOrgType } from '../../shared/use-org-type-terms';
import { importStage, reportFileName } from './import-state';

type Step = 'file' | 'mapping' | 'preview' | 'result';
const STEPS: Step[] = ['file', 'mapping', 'preview', 'result'];

type RowAction = ImportRow['action'];

/** Rijnummers hierboven komen niet uit het bestand (uitdiensten); zie import.service.ts. */
const SYNTHETIC_ROW_START = 1_000_000;

/** Nieuw groen, Wijzigt blauw, Uit dienst oranje, Fout rood (O12). */
const ACTION_TONE: Record<RowAction, BadgeTone> = {
  create: 'ok',
  update: 'info',
  deactivate: 'attention',
  skip: 'neutral',
  error: 'danger',
};

/**
 * De importwizard: bestand → kolommen → voorbeeld → resultaat (O11, O12).
 *
 * Vier stappen op één pagina, want HR doorloopt ze in één zitting en wil terug kunnen.
 * De stap die er het meest toe doet, is de derde: daar staat wat er gaat gebeuren, en
 * daarna pas de knop die het doet.
 */
export function ImportsPage() {
  const { t } = useTranslation();
  const { api } = useSession();
  const queryClient = useQueryClient();

  const [step, setStep] = useState<Step>('file');
  const [job, setJob] = useState<ImportJob | null>(null);
  const [options, setOptions] = useState<MappingOptions | null>(null);
  const [mapping, setMapping] = useState<ImportMapping>({});
  const [mode, setMode] = useState<'changes_only' | 'full_list'>('changes_only');
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [rowFilter, setRowFilter] = useState<string>('');
  const [bekeken, setBekeken] = useState<ImportJob | null>(null);

  const history = useQuery({
    queryKey: ['import', 'jobs'],
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/imports');
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as { items: ImportJob[] }).items;
    },
  });

  const templates = useQuery({
    queryKey: ['import', 'templates'],
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/import-templates');
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as { items: MappingTemplate[] }).items;
    },
  });

  /*
    De voorbeeldrijen met "Meer laden" (punt 10).

    De cursor zat al in het antwoord; het scherm toonde alleen de eerste pagina en zei er niets
    over. Bij een import van vierhonderd rijen betekende dat: je controleert er vijftig en drukt op
    bevestigen voor alle vierhonderd.
  */
  const rows = useInfiniteQuery({
    queryKey: ['import', 'rows', job?.id, rowFilter],
    enabled: step === 'preview' && job !== null,
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (laatste: ImportRowPage) => laatste.nextCursor ?? undefined,
    queryFn: async ({ pageParam }) => {
      const { data, error } = await api.GET('/api/v1/imports/{jobId}/rows', {
        params: {
          path: { jobId: job?.id ?? '' },
          query: {
            ...(rowFilter ? { action: rowFilter } : {}),
            ...(pageParam === undefined ? {} : { cursor: pageParam }),
          },
        },
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return data as ImportRowPage;
    },
  });

  const voorbeeldRijen = (rows.data?.pages ?? []).flatMap((pagina) => pagina.items);

  /** Tijdens het verwerken elke seconde kijken hoe ver het is. */
  const progress = useQuery({
    queryKey: ['import', 'job', job?.id],
    enabled: step === 'result' && job !== null,
    refetchInterval: (query) => {
      const data = query.state.data as ImportJob | undefined;
      return data && (data.status === 'completed' || data.status === 'failed') ? false : 1000;
    },
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/imports/{jobId}', {
        params: { path: { jobId: job?.id ?? '' } },
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return data as ImportJob;
    },
  });

  // Klaar of mislukt: de geschiedenis eronder toont anders nog "wordt verwerkt".
  const progressStatus = progress.data?.status;
  useEffect(() => {
    if (progressStatus === 'completed' || progressStatus === 'failed') {
      void queryClient.invalidateQueries({ queryKey: ['import', 'jobs'] });
    }
  }, [progressStatus, queryClient]);

  const reset = () => {
    setStep('file');
    setJob(null);
    setOptions(null);
    setMapping({});
    setPreview(null);
    setRowFilter('');
    void queryClient.invalidateQueries({ queryKey: ['import'] });
    void queryClient.invalidateQueries({ queryKey: ['directory'] });
  };

  return (
    <AdminPage>
      <PageHead
        title={t('imports.title')}
        sub={
          job && step !== 'file'
            ? t('beheer.organisatie.imports.subFile', { file: job.fileName, count: job.rowCount })
            : t('beheer.organisatie.imports.sub')
        }
      />

      <Steps
        steps={STEPS.map((item) => t(`imports.steps.${item}`))}
        current={STEPS.indexOf(step)}
      />

      {step === 'file' ? (
        <FileStep
          onUploaded={(result) => {
            setJob(result.job);
            setOptions(result.options);
            setMapping(
              Object.fromEntries(
                result.options.suggestions
                  .filter((suggestion) => suggestion.target)
                  .map((suggestion) => [suggestion.column, suggestion.target as string]),
              ),
            );
            setStep('mapping');
          }}
        />
      ) : null}

      {step === 'mapping' && job && options ? (
        <MappingStep
          job={job}
          options={options}
          mapping={mapping}
          mode={mode}
          templates={templates.data ?? []}
          onMappingChange={setMapping}
          onModeChange={setMode}
          onBack={reset}
          onReady={(result) => {
            setPreview(result);
            // Een nieuw voorbeeld van dezelfde job: de rijen van het vorige niet uit de cache tonen.
            queryClient.removeQueries({ queryKey: ['import', 'rows', job.id] });
            setStep('preview');
          }}
        />
      ) : null}

      {step === 'preview' && job && preview ? (
        <PreviewStep
          job={job}
          preview={preview}
          rows={voorbeeldRijen}
          rowsLoading={rows.isPending}
          rowsFailed={rows.isError}
          onRetryRows={() => void rows.refetch()}
          hasMore={rows.hasNextPage}
          loadingMore={rows.isFetchingNextPage}
          onMore={() => void rows.fetchNextPage()}
          filter={rowFilter}
          onFilter={setRowFilter}
          onBack={() => setStep('mapping')}
          onCommitted={() => setStep('result')}
        />
      ) : null}

      {step === 'result' && job ? (
        <Card testId="import-result">
          <CardHead title={t('imports.steps.result')} />
          <CardPad>
            {/* Net bevestigd: tot de eerste stand binnen is, heeft `job` nog de status van vóór. */}
            <ResultBody job={progress.data ?? job} committed onDone={reset} />
          </CardPad>
        </Card>
      ) : null}

      <Card>
        <CardHead title={t('beheer.organisatie.imports.history')} />
        {history.isPending ? (
          <CardPad>
            <LoadingState rows={3} label={t('common.loading')} />
          </CardPad>
        ) : history.isError ? (
          <CardPad>
            <ListError onRetry={() => void history.refetch()} error={history.error} />
          </CardPad>
        ) : (history.data?.length ?? 0) === 0 ? (
          <Empty
            icon={<AdminIcon name="history" size={24} />}
            title={t('imports.noHistory')}
            text={t('beheer.organisatie.imports.noHistoryText')}
          />
        ) : (
          <Table label={t('beheer.organisatie.imports.history')}>
            <thead>
              <tr>
                <th>{t('imports.file')}</th>
                <th>{t('imports.when')}</th>
                <th>{t('imports.status')}</th>
                <th>{t('imports.result')}</th>
              </tr>
            </thead>
            <tbody>
              {history.data?.map((item) => (
                <tr key={item.id} data-testid="import-history-row">
                  <td>
                    {/*
                      Klikbaar (punt 17): de samenvatting en het rapport van een oude import waren
                      alleen te zien zolang je het scherm niet verliet. Wie de dag erna vroeg "wat
                      ging er mis", kon nergens meer kijken.
                    */}
                    <button
                      type="button"
                      data-testid="import-history-open"
                      className="a-link"
                      title={t('imports.historyOpen')}
                      onClick={() => setBekeken(item)}
                    >
                      {item.fileName}
                    </button>
                  </td>
                  <td>{formatDateTime(item.createdAt)}</td>
                  <td>
                    {/* Rood bij `failed` (punt 3): grijs leest als "nog bezig". */}
                    <Badge
                      tone={
                        item.status === 'completed'
                          ? 'ok'
                          : item.status === 'failed'
                            ? 'danger'
                            : 'neutral'
                      }
                    >
                      {t(`imports.statuses.${item.status}`)}
                    </Badge>
                  </td>
                  {/* De tellingen van een voorbeeld zijn een plan, geen resultaat. */}
                  <td>
                    {importStage(item.status) === 'notCommitted'
                      ? t('imports.notCommittedTitle')
                      : item.summary
                        ? summaryText(item.summary, t)
                        : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      {/*
        Het detail van een oude import (punt 17).

        Dezelfde inhoud als bij een verse import, in een venster: zo staat de samenvatting en het
        rapport op één plek, en hoeft er geen tweede weergave onderhouden te worden die langzaam
        van de eerste gaat afwijken.
      */}
      {bekeken ? (
        <Modal
          open
          wide
          onClose={() => setBekeken(null)}
          title={bekeken.fileName}
          sub={formatDateTime(bekeken.createdAt)}
        >
          <ResultBody job={bekeken} onDone={() => setBekeken(null)} />
        </Modal>
      ) : null}
    </AdminPage>
  );
}

/** Stap 1 (O11): de sleepzone met de knop "Bestand kiezen". */
function FileStep({ onUploaded }: { onUploaded: (result: UploadResult) => void }) {
  const { t } = useTranslation();
  const { getAccessToken } = useSession();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const orgType = useOrgType();

  const submit = async (file: File) => {
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', file);
      // Multipart gaat buiten de gegenereerde client om: die stuurt JSON. Het token komt
      // uit de sessie en niet uit een verse refresh — dat laatste zou het refresh token
      // roteren terwijl de applicatie hetzelfde doet.
      const response = await fetch(`${API_BASE}/api/v1/imports`, {
        method: 'POST',
        headers: { authorization: `Bearer ${getAccessToken() ?? ''}` },
        body: form,
      });
      if (!response.ok) {
        /*
          Een foutantwoord is niet altijd JSON (punt 17).

          Een bestand dat te groot is, wordt door de proxy geweigerd met een HTML-pagina; een
          time-out geeft helemaal niets. `response.json()` gooide dan een SyntaxError die niemand
          opving, en het hele scherm klapte dicht op een fout die de gebruiker zelf kon oplossen.
        */
        toast.show({
          title: await foutmelding(response, t('imports.uploadFailed')),
          tone: 'danger',
        });
        return;
      }
      onUploaded((await response.json()) as UploadResult);
    } finally {
      setBusy(false);
    }
  };

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    const file = event.dataTransfer.files?.[0];
    if (file && !busy) void submit(file);
  };

  /*
    Een eigen sleepzone in de vorm van O11 (een lege toestand met een gestippelde rand), en niet
    meer `FileUpload` uit `@up2d8/ui`. Wat die component goed deed, blijft: slepen is nooit de enige
    weg (de knop opent de kiezer), het opschrift komt uit onze vertaling en niet van het
    besturingssysteem, en tijdens het inlezen staat er dat het bezig is.
  */
  return (
    <Card>
      <CardPad>
        <div
          className={cn('a-empty org-drop', dragging && 'on')}
          onDragOver={(event) => {
            event.preventDefault();
            if (!busy) setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
        >
          <div className="a-empty-icon">
            <AdminIcon name="upload" size={24} />
          </div>
          <div className="a-empty-title">{t('beheer.organisatie.imports.dropTitle')}</div>
          <div className="a-empty-text">{t('imports.fileHint')}</div>
          <div style={{ marginTop: 8 }}>
            <Btn
              variant="sec"
              icon={<AdminIcon name="file" />}
              disabled={busy}
              onClick={() => inputRef.current?.click()}
            >
              {t('imports.chooseFile')}
            </Btn>
          </div>
          {busy ? (
            <p className="a-small a-muted" role="status">
              {t('beheer.organisatie.imports.uploading')}
            </p>
          ) : null}
          {/* Een vereniging begint met een leeg bestand in de juiste vorm (VER-1). */}
          {orgType === 'association' ? (
            <p className="a-small">
              <a href="/sjablonen/leden-vereniging.csv" download data-testid="association-template">
                {t('beheer.vereniging.template')}
              </a>
            </p>
          ) : null}
          <input
            ref={inputRef}
            type="file"
            accept=".csv,.xlsx,.xls,text/csv"
            className="a-sr"
            tabIndex={-1}
            aria-hidden="true"
            onChange={(event) => {
              const file = event.target.files?.[0];
              // Leegmaken, zodat hetzelfde bestand nog eens kiezen ook iets doet.
              event.target.value = '';
              if (file) void submit(file);
            }}
          />
        </div>
      </CardPad>
    </Card>
  );
}

/**
 * De tekst van een foutantwoord, wat voor antwoord het ook is.
 *
 * Geeft de server een probleemdocument, dan komt de eerste regel daaruit; geeft hij HTML of
 * niets, dan de meegegeven terugval. Nooit een uitzondering: dit draait in een `catch`-loos pad.
 */
async function foutmelding(response: Response, terugval: string): Promise<string> {
  try {
    const probleem = (await response.json()) as {
      errors?: { message: string }[];
      detail?: string;
    };
    return probleem.errors?.[0]?.message ?? probleem.detail ?? terugval;
  } catch {
    return terugval;
  }
}

/**
 * Stap 2: per kolom uit het bestand een keuzelijst met het veld, een voorbeeldwaarde ernaast
 * (niet getekend; de spec beschrijft het). Daaronder wat het bestand doet en de sjablonen.
 */
function MappingStep({
  job,
  options,
  mapping,
  mode,
  templates,
  onMappingChange,
  onModeChange,
  onBack,
  onReady,
}: {
  job: ImportJob;
  options: MappingOptions;
  mapping: ImportMapping;
  mode: 'changes_only' | 'full_list';
  templates: MappingTemplate[];
  onMappingChange: (mapping: ImportMapping) => void;
  onModeChange: (mode: 'changes_only' | 'full_list') => void;
  onBack: () => void;
  onReady: (preview: PreviewResult) => void;
}) {
  const { t } = useTranslation();
  const { api } = useSession();
  const toast = useToast();
  const [templateName, setTemplateName] = useState('');
  const [teVerwijderen, setTeVerwijderen] = useState<MappingTemplate | null>(null);
  const [gekozenSjabloon, setGekozenSjabloon] = useState('');
  const queryClient = useQueryClient();

  const grouped = useMemo(() => {
    const byGroup = new Map<string, { value: string; label: string }[]>();
    for (const target of options.targets) {
      // Een gegevensveld in de taal van de lezer; de vaste doelen hebben één naam.
      const shown = { value: target.value, label: localized(target.labels) ?? target.label };
      byGroup.set(target.group, [...(byGroup.get(target.group) ?? []), shown]);
    }
    return [...byGroup.entries()];
  }, [options.targets]);

  const run = useMutation({
    mutationFn: async () => {
      const mapped = await api.PUT('/api/v1/imports/{jobId}/mapping', {
        params: { path: { jobId: job.id } },
        body: { mapping, mode } as never,
      });
      if (mapped.error) throw mapped.error;

      const preview = await api.POST('/api/v1/imports/{jobId}/preview', {
        params: { path: { jobId: job.id } },
      });
      if (preview.error || !preview.data) throw preview.error ?? new Error('Mislukt');
      return preview.data as PreviewResult;
    },
    onSuccess: onReady,
    onError: (error) => {
      const detail = (error as { errors?: { message: string }[] }).errors?.[0]?.message;
      toast.show({ title: detail ?? t('imports.mappingFailed'), tone: 'danger' });
    },
  });

  const saveTemplate = useMutation({
    mutationFn: async () => {
      const { error } = await api.POST('/api/v1/import-templates', {
        body: { name: templateName.trim(), mapping } as never,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      setTemplateName('');
      void queryClient.invalidateQueries({ queryKey: ['import', 'templates'] });
      toast.show({ title: t('imports.templateSaved'), tone: 'success' });
    },
    onError: () => toast.show({ title: t('imports.templateFailed'), tone: 'danger' }),
  });

  const removeTemplate = useMutation({
    mutationFn: async (templateId: string) => {
      const { error } = await api.DELETE('/api/v1/import-templates/{templateId}', {
        params: { path: { templateId } },
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      setTeVerwijderen(null);
      await queryClient.invalidateQueries({ queryKey: ['import', 'templates'] });
    },
    onError: () => toast.show({ title: t('imports.templateFailed'), tone: 'danger' }),
  });

  return (
    <Card>
      <CardHead
        title={t('imports.steps.mapping')}
        count={t('imports.rowCount', { count: job.rowCount })}
      />
      <CardPad>
        <p className="a-muted">{t('beheer.organisatie.imports.mappingSub')}</p>
        {templates.length > 0 ? (
          <div className="flex flex-wrap items-end gap-[12px]">
            <Field label={t('imports.useTemplate')} className="min-w-56 flex-1">
              {(props) => (
                <Select
                  {...props}
                  data-testid="template-select"
                  value={gekozenSjabloon}
                  onChange={(event) => {
                    setGekozenSjabloon(event.target.value);
                    const template = templates.find((item) => item.id === event.target.value);
                    if (template) onMappingChange(template.mapping);
                  }}
                >
                  <option value="">{t('imports.chooseTemplate')}</option>
                  {templates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            {/*
              Verwijderen kan pas als er een sjabloon gekozen is (punt 17).

              De keuzelijst stond eerst op `value=""` en vergat meteen wat je koos - handig om een
              toewijzing te laden, onbruikbaar om er iets mee te doen. Nu onthoudt ze het, en staat
              de knop ernaast.
            */}
            {gekozenSjabloon ? (
              <Btn
                variant="ghost"
                testId="template-remove"
                onClick={() =>
                  setTeVerwijderen(templates.find((item) => item.id === gekozenSjabloon) ?? null)
                }
              >
                {t('imports.templateDelete')}
              </Btn>
            ) : null}
          </div>
        ) : null}
      </CardPad>

      <ConfirmModal
        open={teVerwijderen !== null}
        onClose={() => setTeVerwijderen(null)}
        title={t('imports.templateDelete')}
        description={t('imports.templateDeleteConfirm', { name: teVerwijderen?.name ?? '' })}
        confirmLabel={t('common.delete')}
        busy={removeTemplate.isPending}
        testId="confirm-template-remove"
        onConfirm={() => {
          if (teVerwijderen) removeTemplate.mutate(teVerwijderen.id);
          setGekozenSjabloon('');
        }}
      />

      <Table label={t('imports.steps.mapping')}>
        <thead>
          <tr>
            <th>{t('imports.column')}</th>
            <th>{t('imports.example')}</th>
            <th>{t('imports.target')}</th>
          </tr>
        </thead>
        <tbody>
          {options.suggestions.map((suggestion) => (
            <tr key={suggestion.column}>
              <td>
                <span className="a-name">{suggestion.column}</span>
              </td>
              <td className="a-muted">{suggestion.sample ?? '—'}</td>
              <td>
                <Select
                  aria-label={t('imports.targetFor', { column: suggestion.column })}
                  value={mapping[suggestion.column] ?? ''}
                  onChange={(event) => {
                    const next = { ...mapping };
                    if (event.target.value) next[suggestion.column] = event.target.value;
                    else delete next[suggestion.column];
                    onMappingChange(next);
                  }}
                >
                  <option value="">{t('imports.ignore')}</option>
                  {grouped.map(([group, targets]) => (
                    <optgroup key={group} label={group}>
                      {targets.map((target) => (
                        <option key={target.value} value={target.value}>
                          {target.label}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </Select>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <CardPad>
        <Field label={t('imports.mode')} hint={t('imports.modeHint')}>
          {(props) => (
            <Select
              {...props}
              value={mode}
              onChange={(event) => onModeChange(event.target.value as 'changes_only' | 'full_list')}
            >
              <option value="changes_only">{t('imports.modeChanges')}</option>
              <option value="full_list">{t('imports.modeFull')}</option>
            </Select>
          )}
        </Field>

        <div className="flex flex-wrap items-end gap-[12px]">
          <Field label={t('imports.saveTemplate')}>
            {(props) => (
              <Input
                {...props}
                value={templateName}
                onChange={(event) => setTemplateName(event.target.value)}
              />
            )}
          </Field>
          <Btn
            variant="sec"
            disabled={templateName.trim().length < 2 || saveTemplate.isPending}
            onClick={() => saveTemplate.mutate()}
          >
            {t('common.save')}
          </Btn>
        </div>
      </CardPad>

      <CardFoot>
        <span />
        <div className="a-actions">
          <Btn variant="sec" onClick={onBack}>
            {t('common.back')}
          </Btn>
          <Btn disabled={run.isPending} onClick={() => run.mutate()}>
            {t('imports.toPreview')}
          </Btn>
        </div>
      </CardFoot>
    </Card>
  );
}

/** De drie kerncijfers (O12): wat er gaat gebeuren, of met `done` wat er gebeurde. */
function ImportStats({
  summary,
  done,
}: {
  summary: { create: number; update: number; deactivate: number };
  done?: boolean;
}) {
  const { t } = useTranslation();
  const k = 'beheer.organisatie.imports.stat';
  return (
    <Stats
      label={t('beheer.organisatie.imports.statsLabel')}
      items={[
        {
          label: t(`${k}.create`),
          value: summary.create,
          sub: t(done ? `${k}.createdSub` : `${k}.createSub`),
          testId: 'import-stat-create',
        },
        {
          label: t(`${k}.update`),
          value: summary.update,
          sub: t(done ? `${k}.updatedSub` : `${k}.updateSub`),
          testId: 'import-stat-update',
        },
        {
          label: t(`${k}.deactivate`),
          value: summary.deactivate,
          sub: t(done ? `${k}.deactivatedSub` : `${k}.deactivateSub`),
          testId: 'import-stat-deactivate',
        },
      ]}
    />
  );
}

/** Stap 3 (O12): drie cijfers, dan elke wijziging per rij, en pas dan de knop. */
function PreviewStep({
  job,
  preview,
  rows,
  rowsLoading,
  rowsFailed,
  onRetryRows,
  hasMore,
  loadingMore,
  onMore,
  filter,
  onFilter,
  onBack,
  onCommitted,
}: {
  job: ImportJob;
  preview: PreviewResult;
  rows: ImportRow[];
  rowsLoading: boolean;
  rowsFailed: boolean;
  onRetryRows: () => void;
  hasMore: boolean;
  loadingMore: boolean;
  onMore: () => void;
  filter: string;
  onFilter: (value: string) => void;
  onBack: () => void;
  onCommitted: () => void;
}) {
  const { t } = useTranslation();
  const { api } = useSession();
  const toast = useToast();
  const [confirmed, setConfirmed] = useState(false);

  const { summary } = preview;
  const needsConfirmation = summary.deactivate > 0;
  const wijzigingen = summary.create + summary.update + summary.deactivate;

  const commit = useMutation({
    mutationFn: async () => {
      const { error } = await api.POST('/api/v1/imports/{jobId}/commit', {
        params: { path: { jobId: job.id } },
        body: { expectedDeactivations: summary.deactivate } as never,
      });
      if (error) throw error;
    },
    onSuccess: onCommitted,
    onError: (error) =>
      toast.show({
        title: problemCode(error) === 'CONFLICT' ? t('imports.changed') : t('imports.commitFailed'),
        tone: 'danger',
      }),
  });

  const acties: RowAction[] = ['create', 'update', 'deactivate', 'skip', 'error'];

  return (
    <>
      <Card>
        <CardPad>
          <ImportStats summary={summary} />
        </CardPad>
      </Card>

      <Card testId="import-preview">
        <CardHead
          title={t('beheer.organisatie.imports.changes')}
          count={wijzigingen + summary.error}
          actions={
            <FilterMenu
              label={t('beheer.organisatie.imports.show')}
              value={filter || 'all'}
              onChange={(value) => onFilter(value === 'all' ? '' : value)}
              alwaysShowValue
              testId="import-row-filter"
              options={[
                { value: 'all', label: t('beheer.organisatie.all') },
                ...acties.map((action) => ({
                  value: action,
                  label: t(`beheer.organisatie.imports.action.${action}`),
                })),
              ]}
            />
          }
        />

        {preview.newGroups.length > 0 ? (
          <CardPad>
            <Note tone="info">
              {t('imports.newGroups', {
                groups: preview.newGroups.map((group) => group.name).join(', '),
              })}
            </Note>
          </CardPad>
        ) : null}

        {rowsLoading ? (
          <CardPad>
            <LoadingState rows={4} label={t('common.loading')} />
          </CardPad>
        ) : rowsFailed ? (
          <CardPad>
            <ListError onRetry={onRetryRows} />
          </CardPad>
        ) : rows.length === 0 ? (
          <Empty
            icon={<AdminIcon name="search" size={24} />}
            title={t('beheer.organisatie.imports.noRows')}
          />
        ) : (
          <Table label={t('beheer.organisatie.imports.changes')}>
            <thead>
              <tr>
                <th>{t('beheer.organisatie.imports.colRow')}</th>
                <th>{t('beheer.organisatie.imports.colNumber')}</th>
                <th>{t('beheer.organisatie.imports.colName')}</th>
                <th>{t('beheer.organisatie.imports.colWhat')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const uitleg = row.action === 'error' ? row.errorDetail : row.detail;
                return (
                  <tr key={`${row.rowNumber}-${row.employeeNumber ?? row.displayName}`}>
                    {/* Wie uit dienst gaat, staat niet in het bestand: geen rijnummer. De API
                        nummert die rijen boven SYNTHETIC_ROW_START. */}
                    <td>
                      {row.rowNumber > 0 && row.rowNumber <= SYNTHETIC_ROW_START
                        ? row.rowNumber
                        : '—'}
                    </td>
                    <td>
                      <span className="a-mono">{row.employeeNumber ?? '—'}</span>
                    </td>
                    <td>{row.displayName}</td>
                    <td>
                      <Badge tone={ACTION_TONE[row.action]}>
                        {t(`beheer.organisatie.imports.action.${row.action}`)}
                      </Badge>{' '}
                      {uitleg ? <span className="a-meta">{uitleg}</span> : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}

        {hasMore ? (
          <CardPad className="items-center">
            <Btn variant="sec" size="sm" testId="rows-more" disabled={loadingMore} onClick={onMore}>
              {t('people.loadMore')}
            </Btn>
          </CardPad>
        ) : null}

        {needsConfirmation ? (
          <CardPad>
            {/* Uitdiensten vragen een expliciete tweede bevestiging, met het aantal erbij. */}
            <Note tone="attention">
              <Checkbox
                checked={confirmed}
                onChange={setConfirmed}
                label={t('imports.confirmDeactivations', { count: summary.deactivate })}
              />
            </Note>
          </CardPad>
        ) : null}

        <CardFoot>
          <span>
            {summary.error > 0
              ? t('beheer.organisatie.imports.errorsSkipped', { count: summary.error })
              : t('beheer.organisatie.imports.noErrors')}
          </span>
          <div className="a-actions">
            <Btn variant="sec" onClick={onBack}>
              {t('common.back')}
            </Btn>
            <Btn
              icon={<AdminIcon name="check" />}
              testId="import-commit"
              disabled={commit.isPending || (needsConfirmation && !confirmed)}
              onClick={() => commit.mutate()}
            >
              {t('beheer.organisatie.imports.commit', { count: wijzigingen })}
            </Btn>
          </div>
        </CardFoot>
      </Card>
    </>
  );
}

/** Stap 4 (niet getekend): dezelfde drie cijfers als gebeurd, het rapport en de activatiebrieven. */
function ResultBody({
  job,
  committed = false,
  onDone,
}: {
  job: ImportJob;
  /** Net bevestigd in de wizard: een oude status telt dan als "bezig", niet als "niet doorgevoerd". */
  committed?: boolean;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const { api, getAccessToken } = useSession();
  const toast = useToast();
  const [letterId, setLetterId] = useState<string | null>(null);
  const [letterFormat, setLetterFormat] = useState<'pdf' | 'csv'>('pdf');

  const letters = useQuery({
    queryKey: ['import', 'letters', letterId],
    enabled: letterId !== null,
    refetchInterval: (query) => {
      const data = query.state.data as { status: string } | undefined;
      return data && data.status !== 'processing' ? false : 1000;
    },
    queryFn: async () => {
      const { data, error } = await api.GET(
        '/api/v1/imports/{jobId}/activation-letters/{letterId}',
        { params: { path: { jobId: job.id, letterId: letterId ?? '' } } },
      );
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return data as { id: string; status: string; personCount: number };
    },
  });

  const request = useMutation({
    mutationFn: async (format: 'pdf' | 'csv') => {
      const { data, error } = await api.POST('/api/v1/imports/{jobId}/activation-letters', {
        params: { path: { jobId: job.id } },
        body: { format } as never,
      });
      if (error || !data) throw error ?? new Error('Mislukt');
      return data as { id: string };
    },
    // Het gekozen formaat onthouden: de bestandsnaam van de download hangt ervan af.
    onSuccess: (batch, format) => {
      setLetterFormat(format);
      setLetterId(batch.id);
    },
    onError: () => toast.show({ title: t('imports.lettersFailed'), tone: 'danger' }),
  });

  const downloadRapport = useMutation({
    mutationFn: () =>
      downloadFile({
        path: `/api/v1/imports/${job.id}/report.csv`,
        fileName: reportFileName(job.fileName),
        token: getAccessToken(),
        baseUrl: API_BASE,
      }),
    onError: () => toast.show({ title: t('errors.generic'), tone: 'danger' }),
  });

  const downloadBrieven = useMutation({
    mutationFn: () =>
      downloadFile({
        path: `/api/v1/imports/${job.id}/activation-letters/${letterId ?? ''}/download`,
        fileName: `activatiebrieven.${letterFormat}`,
        token: getAccessToken(),
        baseUrl: API_BASE,
      }),
    onError: () => toast.show({ title: t('errors.generic'), tone: 'danger' }),
  });

  /*
    `failed` apart van "nog niet klaar" (punt 3), en sinds de test van 28 okt. ook "nooit
    bevestigd" apart van "bezig".

    Dit scherm kende alleen `completed`, en alles wat dat niet was, kreeg de voortgangsbalk. Een
    import die stukliep, of die iemand alleen bekeek, bleef dus eeuwig op "bezig" staan - en wie
    dat ziet, wacht, en wacht.
  */
  const stage = (() => {
    const fromStatus = importStage(job.status);
    return committed && fromStatus === 'notCommitted' ? 'running' : fromStatus;
  })();
  const done = stage === 'done';
  const failed = stage === 'failed';
  const percentage = job.rowCount > 0 ? Math.round((job.processedCount / job.rowCount) * 100) : 0;

  if (failed) {
    return (
      <div className="flex flex-col gap-[14px]" data-testid="import-failed">
        <Note tone="danger" lead={t('imports.failedTitle')}>
          {/* De reden van de server als die er is; anders een zin die zegt dat we het niet
              weten, en niet een verzonnen oorzaak. */}
          {job.errorDetail ?? t('imports.failedUnknown')}
        </Note>
        <div>
          <Btn onClick={onDone}>{t('imports.newImport')}</Btn>
        </div>
      </div>
    );
  }

  if (stage === 'notCommitted') {
    return (
      <div className="flex flex-col gap-[14px]" data-testid="import-not-committed">
        <Note tone="attention" lead={t('imports.notCommittedTitle')}>
          {t('imports.notCommittedText')}
        </Note>
        <div>
          <Btn onClick={onDone}>{t('imports.newImport')}</Btn>
        </div>
      </div>
    );
  }

  if (!done) {
    return (
      <div className="flex flex-col gap-[8px]">
        <p className="a-muted">
          {t('imports.processing', { done: job.processedCount, total: job.rowCount })}
        </p>
        <div
          className="a-bar"
          role="progressbar"
          aria-valuenow={percentage}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={t('imports.steps.result')}
        >
          <span style={{ width: `${percentage}%` }} />
        </div>
      </div>
    );
  }

  return (
    <>
      {job.summary ? <ImportStats summary={job.summary} done /> : null}
      {/* De zin met ook de overgeslagen en foute rijen, zoals in de lijst van vorige imports. */}
      <p className="a-muted" data-testid="import-result-summary">
        {job.summary ? summaryText(job.summary, t) : '—'}
      </p>

      <div className="a-actions">
        {/*
          Een knop en geen `<a href download>`: die laatste is een navigatie van de browser en
          stuurt geen `Authorization`-header mee, dus gaf hij altijd een 401 (punt 1).
        */}
        <Btn
          variant="sec"
          testId="download-report"
          disabled={downloadRapport.isPending}
          onClick={() => downloadRapport.mutate()}
        >
          {t('imports.downloadReport')}
        </Btn>
        <Btn
          variant="sec"
          icon={<AdminIcon name="printer" />}
          disabled={request.isPending}
          onClick={() => request.mutate('pdf')}
        >
          {t('beheer.organisatie.imports.lettersPrint')}
        </Btn>
        <Btn variant="ghost" disabled={request.isPending} onClick={() => request.mutate('csv')}>
          {t('imports.lettersCsv')}
        </Btn>
      </div>

      {letterId ? (
        <div>
          {letters.data?.status === 'ready' ? (
            <Btn
              variant="ghost"
              size="sm"
              testId="download-letters"
              disabled={downloadBrieven.isPending}
              onClick={() => downloadBrieven.mutate()}
            >
              {t('imports.lettersReady', { count: letters.data.personCount })}
            </Btn>
          ) : letters.data?.status === 'failed' ? (
            // Ook hier: een batch die stukliep, hoort niet "bezig" te blijven heten.
            <Note tone="danger" testId="letters-failed">
              {t('imports.lettersFailed')}
            </Note>
          ) : (
            <p className="a-muted" role="status">
              {t('imports.lettersBusy')}
            </p>
          )}
        </div>
      ) : null}

      <div>
        <Btn onClick={onDone}>{t('imports.newImport')}</Btn>
      </div>
    </>
  );
}

function summaryText(
  summary: { create: number; update: number; deactivate: number; skip: number; error: number },
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  return t('imports.summary', {
    create: summary.create,
    update: summary.update,
    deactivate: summary.deactivate,
    error: summary.error,
  });
}
